"use client"

import { useState } from "react"
import { Loader2, CheckCircle, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"

export function InlineCareerForm() {
    const [formData, setFormData] = useState({
        candidateName: "",
        fatherName: "",
        gender: "",
        dob: "",
        mobileNo: "",
        email: "",
        qualification: "",
        message: "",
        resume: null as File | null,
    })

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitStatus, setSubmitStatus] = useState<{
        type: "success" | "error" | null
        message: string
    }>({ type: null, message: "" })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)
        setSubmitStatus({ type: null, message: "" })

        try {
            const submitData = new FormData()

            // Map fields to exactly match PHP script & SQL schema
            submitData.append('name', formData.candidateName)
            submitData.append('fname', formData.fatherName)
            submitData.append('gender', formData.gender)
            submitData.append('dob', formData.dob)
            submitData.append('phno', formData.mobileNo)
            submitData.append('email', formData.email)
            submitData.append('std', formData.qualification)
            submitData.append('msg', formData.message)

            if (formData.resume) {
                // Usually PHP expects 'resume' for the file input
                submitData.append('resume', formData.resume)
            }

            // Get current date in YYYY-MM-DD format if needed by API
            const today = new Date().toISOString().split('T')[0]
            submitData.append('date', today)

            console.log("📤 Submitting career application via proxy...")

            const response = await fetch("/api/career", {
                method: "POST",
                body: submitData,
            })

            const result = await response.json()
            console.log("📥 API Response:", result)

            if (response.ok && (result.success || result.status === 'success' || result.status === '1')) {
                setSubmitStatus({
                    type: "success",
                    message: "Career application submitted successfully! Our HR team will review your profile.",
                })
                // Reset form
                setFormData({
                    candidateName: "",
                    fatherName: "",
                    gender: "",
                    dob: "",
                    mobileNo: "",
                    email: "",
                    qualification: "",
                    message: "",
                    resume: null,
                })
            } else {
                throw new Error(result.message || "The server rejected the application.")
            }

        } catch (error: any) {
            console.error("❌ Submission error:", error)
            setSubmitStatus({
                type: "error",
                message: error.message || "Failed to submit application. Please try again later.",
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setFormData((prev) => ({ ...prev, resume: file }))
    }

    return (
        <div className="bg-white">
            {submitStatus.type && (
                <div
                    className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${submitStatus.type === "success"
                        ? "bg-green-50 text-green-800 border border-green-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                        }`}
                >
                    {submitStatus.type === "success" ? (
                        <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    ) : (
                        <XCircle className="h-5 w-5 flex-shrink-0" />
                    )}
                    <p>{submitStatus.message}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Candidate Name */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Candidate Name
                    </label>
                    <input
                        type="text"
                        name="candidateName"
                        value={formData.candidateName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                {/* Father Name */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Father Name
                    </label>
                    <input
                        type="text"
                        name="fatherName"
                        value={formData.fatherName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                {/* Gender */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Gender
                    </label>
                    <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-600"
                    >
                        <option value="">Select One</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                </div>

                {/* Date of Birth */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Date of Birth
                    </label>
                    <input
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleInputChange}
                        required
                        placeholder="mm/dd/yyyy"
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-600"
                    />
                </div>

                {/* Mobile No */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Mobile No.
                    </label>
                    <div className="flex border border-gray-300 rounded overflow-hidden">
                        <div className="bg-gray-100 px-3 py-2 border-r border-gray-300 text-sm text-gray-600 whitespace-nowrap">
                            <span className="text-red-600 mr-1">*</span>Contact No.
                        </div>
                        <input
                            type="tel"
                            name="mobileNo"
                            value={formData.mobileNo}
                            onChange={handleInputChange}
                            required
                            className="flex-1 px-4 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {/* Email */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Email
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                {/* Qualification */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Qualification
                    </label>
                    <input
                        type="text"
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                {/* Your Message */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Your Message
                    </label>
                    <input
                        type="text"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                {/* Upload Resume */}
                <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                        <span className="text-red-600 mr-1">*</span>Upload Resume
                    </label>
                    <div className="border border-gray-300 rounded flex items-center overflow-hidden">
                        <button type="button" className="bg-[#e9e9e9] px-3 py-1 border-r border-gray-300 text-sm hover:bg-gray-200">
                            Choose File
                        </button>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            required
                            className="w-full px-3 py-1 text-sm text-gray-500 file:hidden cursor-pointer"
                        />
                    </div>
                </div>

                {/* Submit */}
                <div className="flex justify-center pt-4">
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-[#2a1b12] hover:bg-black text-white px-8 py-2 rounded font-medium border border-[#4a3b32]"
                    >
                        {isSubmitting ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Submitting...
                            </span>
                        ) : (
                            "Submit"
                        )}
                    </Button>
                </div>

            </form>
        </div>
    )
}
